import { ChatWidgetConfig, ChatMessage, WidgetState, LeadFormData } from '../types'
import { ConfigManager } from '../utils/config'
import ApiClient from '../utils/apiClient'
import { createChatBubble } from './ChatBubble'
import { createChatWindow } from './ChatWindow'

export class ChatWidget {
  private config: ConfigManager
  private apiClient: ApiClient
  private state: WidgetState
  private container: HTMLElement | null = null
  private bubble: HTMLElement | null = null
  private window: HTMLElement | null = null

  constructor(userConfig: ChatWidgetConfig) {
    this.config = new ConfigManager(userConfig)
    const apiUrl = userConfig.apiUrl || 'http://localhost:3000/api'
    this.apiClient = new ApiClient(apiUrl)
    
    // Load existing session from localStorage or create new one
    const existingSessionId = this.getStoredSessionId()
    const leadSubmitted = this.getLeadSubmissionStatus()
    
    this.state = {
      isOpen: false,
      isMinimized: false,
      isLoading: false,
      messages: [],
      error: null,
      sessionId: existingSessionId,
      showLeadForm: !leadSubmitted, // Show form only if lead not submitted
      leadSubmitted: leadSubmitted,
    }

    // Listen for suggestion clicks
    window.addEventListener('suggestionClick', (event: any) => {
      const suggestion = event.detail
      if (suggestion && this.state.isOpen) {
        this.sendMessage(suggestion)
      }
    })

    this.init()
  }

  private getStoredSessionId(): string | undefined {
    try {
      const apiKey = this.config.getConfig().apiKey
      const storageKey = `conversea_session_${apiKey}`
      return localStorage.getItem(storageKey) || undefined
    } catch (error) {
      // localStorage might not be available
      return undefined
    }
  }

  private storeSessionId(sessionId: string): void {
    try {
      const apiKey = this.config.getConfig().apiKey
      const storageKey = `conversea_session_${apiKey}`
      localStorage.setItem(storageKey, sessionId)
    } catch (error) {
      // localStorage might not be available
      console.warn('Could not store session ID:', error)
    }
  }

  private clearStoredSession(): void {
    try {
      const apiKey = this.config.getConfig().apiKey
      const storageKey = `conversea_session_${apiKey}`
      localStorage.removeItem(storageKey)
    } catch (error) {
      // localStorage might not be available
    }
  }

  private getLeadSubmissionStatus(): boolean {
    try {
      const apiKey = this.config.getConfig().apiKey
      const storageKey = `conversea_lead_${apiKey}`
      return localStorage.getItem(storageKey) === 'true'
    } catch (error) {
      // localStorage might not be available
      return false
    }
  }

  private setLeadSubmissionStatus(submitted: boolean): void {
    try {
      const apiKey = this.config.getConfig().apiKey
      const storageKey = `conversea_lead_${apiKey}`
      localStorage.setItem(storageKey, submitted.toString())
    } catch (error) {
      // localStorage might not be available
      console.warn('Could not store lead submission status:', error)
    }
  }

  private init(): void {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.render())
    } else {
      this.render()
    }
  }

  private render(): void {
    // Remove any existing conversea widget to prevent duplicates
    const existingWidget = document.getElementById('conversea-widget')
    if (existingWidget) {
      existingWidget.remove()
    }

    // Create container
    this.container = document.createElement('div')
    this.container.id = 'conversea-widget'
    
    // Apply position styles
    const positionStyles = this.config.getPositionStyles()
    Object.assign(this.container.style, positionStyles)

    // Create and add bubble
    this.renderBubble()

    // Add to page
    document.body.appendChild(this.container)

    // Add CSS animations
    this.addStyles()
  }

  private createLeadForm(theme: any): HTMLElement {
    const leadForm = document.createElement('div')
    leadForm.classList.add('chat-window')
    
    const config = this.config.getConfig()
    const { height = '600px', width = '400px' } = config
    
    Object.assign(leadForm.style, {
      position: 'fixed',
      bottom: '90px',
      right: '20px',
      width,
      height,
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      zIndex: '2147483647',
      overflow: 'hidden',
    })

    // Countries data structure from react-phone-input-2
    const countries = [
      ['Afghanistan', ['asia'], 'af', '93', '🇦🇫'],
      ['Albania', ['europe'], 'al', '355', '🇦🇱'],
      ['Algeria', ['africa', 'north-africa'], 'dz', '213', '🇩🇿'],
      ['Andorra', ['europe'], 'ad', '376', '🇦🇩'],
      ['Angola', ['africa'], 'ao', '244', '🇦🇴'],
      ['Antigua and Barbuda', ['america', 'carribean'], 'ag', '1268', '🇦🇬'],
      ['Argentina', ['america', 'south-america'], 'ar', '54', '🇦🇷'],
      ['Armenia', ['asia', 'ex-ussr'], 'am', '374', '🇦🇲'],
      ['Aruba', ['america', 'carribean'], 'aw', '297', '🇦🇼'],
      ['Australia', ['oceania'], 'au', '61', '🇦🇺'],
      ['Austria', ['europe', 'eu-union'], 'at', '43', '🇦🇹'],
      ['Azerbaijan', ['asia', 'ex-ussr'], 'az', '994', '🇦🇿'],
      ['Bahamas', ['america', 'carribean'], 'bs', '1242', '🇧🇸'],
      ['Bahrain', ['middle-east'], 'bh', '973', '🇧🇭'],
      ['Bangladesh', ['asia'], 'bd', '880', '🇧🇩'],
      ['Barbados', ['america', 'carribean'], 'bb', '1246', '🇧🇧'],
      ['Belarus', ['europe', 'ex-ussr'], 'by', '375', '🇧🇾'],
      ['Belgium', ['europe', 'eu-union'], 'be', '32', '🇧🇪'],
      ['Belize', ['america', 'central-america'], 'bz', '501', '🇧🇿'],
      ['Benin', ['africa'], 'bj', '229', '🇧🇯'],
      ['Bhutan', ['asia'], 'bt', '975', '🇧🇹'],
      ['Bolivia', ['america', 'south-america'], 'bo', '591', '🇧🇴'],
      ['Bosnia and Herzegovina', ['europe', 'ex-yugos'], 'ba', '387', '🇧🇦'],
      ['Botswana', ['africa'], 'bw', '267', '🇧🇼'],
      ['Brazil', ['america', 'south-america'], 'br', '55', '🇧🇷'],
      ['British Indian Ocean Territory', ['asia'], 'io', '246', '🇮🇴'],
      ['Brunei', ['asia'], 'bn', '673', '🇧🇳'],
      ['Bulgaria', ['europe', 'eu-union'], 'bg', '359', '🇧🇬'],
      ['Burkina Faso', ['africa'], 'bf', '226', '🇧🇫'],
      ['Burundi', ['africa'], 'bi', '257', '🇧🇮'],
      ['Cambodia', ['asia'], 'kh', '855', '🇰🇭'],
      ['Cameroon', ['africa'], 'cm', '237', '🇨🇲'],
      ['Canada', ['america', 'north-america'], 'ca', '1', '🇨🇦'],
      ['Cape Verde', ['africa'], 'cv', '238', '🇨🇻'],
      ['Caribbean Netherlands', ['america', 'carribean'], 'bq', '599', '🇧🇶'],
      ['Central African Republic', ['africa'], 'cf', '236', '🇨🇫'],
      ['Chad', ['africa'], 'td', '235', '🇹🇩'],
      ['Chile', ['america', 'south-america'], 'cl', '56', '🇨🇱'],
      ['China', ['asia'], 'cn', '86', '🇨🇳'],
      ['Colombia', ['america', 'south-america'], 'co', '57', '🇨🇴'],
      ['Comoros', ['africa'], 'km', '269', '🇰🇲'],
      ['Congo', ['africa'], 'cd', '243', '🇨🇩'],
      ['Congo', ['africa'], 'cg', '242', '🇨🇬'],
      ['Costa Rica', ['america', 'central-america'], 'cr', '506', '🇨🇷'],
      ['Côte d\'Ivoire', ['africa'], 'ci', '225', '🇨🇮'],
      ['Croatia', ['europe', 'eu-union', 'ex-yugos'], 'hr', '385', '🇭🇷'],
      ['Cuba', ['america', 'carribean'], 'cu', '53', '🇨🇺'],
      ['Curaçao', ['america', 'carribean'], 'cw', '599', '🇨🇼'],
      ['Cyprus', ['europe', 'eu-union'], 'cy', '357', '🇨🇾'],
      ['Czech Republic', ['europe', 'eu-union'], 'cz', '420', '🇨🇿'],
      ['Denmark', ['europe', 'eu-union', 'baltic'], 'dk', '45', '🇩🇰'],
      ['Djibouti', ['africa'], 'dj', '253', '🇩🇯'],
      ['Dominica', ['america', 'carribean'], 'dm', '1767', '🇩🇲'],
      ['Dominican Republic', ['america', 'carribean'], 'do', '1', '🇩🇴'],
      ['Ecuador', ['america', 'south-america'], 'ec', '593', '🇪🇨'],
      ['Egypt', ['africa', 'north-africa'], 'eg', '20', '🇪🇬'],
      ['El Salvador', ['america', 'central-america'], 'sv', '503', '🇸🇻'],
      ['Equatorial Guinea', ['africa'], 'gq', '240', '🇬🇶'],
      ['Eritrea', ['africa'], 'er', '291', '🇪🇷'],
      ['Estonia', ['europe', 'eu-union', 'ex-ussr', 'baltic'], 'ee', '372', '🇪🇪'],
      ['Ethiopia', ['africa'], 'et', '251', '🇪🇹'],
      ['Fiji', ['oceania'], 'fj', '679', '🇫🇯'],
      ['Finland', ['europe', 'eu-union', 'baltic'], 'fi', '358', '🇫🇮'],
      ['France', ['europe', 'eu-union'], 'fr', '33', '🇫🇷'],
      ['French Guiana', ['america', 'south-america'], 'gf', '594', '🇬🇫'],
      ['French Polynesia', ['oceania'], 'pf', '689', '🇵🇫'],
      ['Gabon', ['africa'], 'ga', '241', '🇬🇦'],
      ['Gambia', ['africa'], 'gm', '220', '🇬🇲'],
      ['Georgia', ['asia', 'ex-ussr'], 'ge', '995', '🇬🇪'],
      ['Germany', ['europe', 'eu-union', 'baltic'], 'de', '49', '🇩🇪'],
      ['Ghana', ['africa'], 'gh', '233', '🇬🇭'],
      ['Greece', ['europe', 'eu-union'], 'gr', '30', '🇬🇷'],
      ['Grenada', ['america', 'carribean'], 'gd', '1473', '🇬🇩'],
      ['Guadeloupe', ['america', 'carribean'], 'gp', '590', '🇬🇵'],
      ['Guam', ['oceania'], 'gu', '1671', '🇬🇺'],
      ['Guatemala', ['america', 'central-america'], 'gt', '502', '🇬🇹'],
      ['Guinea', ['africa'], 'gn', '224', '🇬🇳'],
      ['Guinea-Bissau', ['africa'], 'gw', '245', '🇬🇼'],
      ['Guyana', ['america', 'south-america'], 'gy', '592', '🇬🇾'],
      ['Haiti', ['america', 'carribean'], 'ht', '509', '🇭🇹'],
      ['Honduras', ['america', 'central-america'], 'hn', '504', '🇭🇳'],
      ['Hong Kong', ['asia'], 'hk', '852', '🇭🇰'],
      ['Hungary', ['europe', 'eu-union'], 'hu', '36', '🇭🇺'],
      ['Iceland', ['europe'], 'is', '354', '🇮🇸'],
      ['India', ['asia'], 'in', '91', '🇮🇳'],
      ['Indonesia', ['asia'], 'id', '62', '🇮🇩'],
      ['Iran', ['middle-east'], 'ir', '98', '🇮🇷'],
      ['Iraq', ['middle-east'], 'iq', '964', '🇮🇶'],
      ['Ireland', ['europe', 'eu-union'], 'ie', '353', '🇮🇪'],
      ['Israel', ['middle-east'], 'il', '972', '🇮🇱'],
      ['Italy', ['europe', 'eu-union'], 'it', '39', '🇮🇹'],
      ['Jamaica', ['america', 'carribean'], 'jm', '1876', '🇯🇲'],
      ['Japan', ['asia'], 'jp', '81', '🇯🇵'],
      ['Jordan', ['middle-east'], 'jo', '962', '🇯🇴'],
      ['Kazakhstan', ['asia', 'ex-ussr'], 'kz', '7', '🇰🇿'],
      ['Kenya', ['africa'], 'ke', '254', '🇰🇪'],
      ['Kiribati', ['oceania'], 'ki', '686', '🇰🇮'],
      ['Kosovo', ['europe', 'ex-yugos'], 'xk', '383', '🇽🇰'],
      ['Kuwait', ['middle-east'], 'kw', '965', '🇰🇼'],
      ['Kyrgyzstan', ['asia', 'ex-ussr'], 'kg', '996', '🇰🇬'],
      ['Laos', ['asia'], 'la', '856', '🇱🇦'],
      ['Latvia', ['europe', 'eu-union', 'ex-ussr', 'baltic'], 'lv', '371', '🇱🇻'],
      ['Lebanon', ['middle-east'], 'lb', '961', '🇱🇧'],
      ['Lesotho', ['africa'], 'ls', '266', '🇱🇸'],
      ['Liberia', ['africa'], 'lr', '231', '🇱🇷'],
      ['Libya', ['africa', 'north-africa'], 'ly', '218', '🇱🇾'],
      ['Liechtenstein', ['europe'], 'li', '423', '🇱🇮'],
      ['Lithuania', ['europe', 'eu-union', 'ex-ussr', 'baltic'], 'lt', '370', '🇱🇹'],
      ['Luxembourg', ['europe', 'eu-union'], 'lu', '352', '🇱🇺'],
      ['Macau', ['asia'], 'mo', '853', '🇲🇴'],
      ['Macedonia', ['europe', 'ex-yugos'], 'mk', '389', '🇲🇰'],
      ['Madagascar', ['africa'], 'mg', '261', '🇲🇬'],
      ['Malawi', ['africa'], 'mw', '265', '🇲🇼'],
      ['Malaysia', ['asia'], 'my', '60', '🇲🇾'],
      ['Maldives', ['asia'], 'mv', '960', '🇲🇻'],
      ['Mali', ['africa'], 'ml', '223', '🇲🇱'],
      ['Malta', ['europe', 'eu-union'], 'mt', '356', '🇲🇹'],
      ['Marshall Islands', ['oceania'], 'mh', '692', '🇲🇭'],
      ['Martinique', ['america', 'carribean'], 'mq', '596', '🇲🇶'],
      ['Mauritania', ['africa'], 'mr', '222', '🇲🇷'],
      ['Mauritius', ['africa'], 'mu', '230', '🇲🇺'],
      ['Mexico', ['america', 'central-america'], 'mx', '52', '🇲🇽'],
      ['Micronesia', ['oceania'], 'fm', '691', '🇫🇲'],
      ['Moldova', ['europe'], 'md', '373', '🇲🇩'],
      ['Monaco', ['europe'], 'mc', '377', '🇲🇨'],
      ['Mongolia', ['asia'], 'mn', '976', '🇲🇳'],
      ['Montenegro', ['europe', 'ex-yugos'], 'me', '382', '🇲🇪'],
      ['Morocco', ['africa', 'north-africa'], 'ma', '212', '🇲🇦'],
      ['Mozambique', ['africa'], 'mz', '258', '🇲🇿'],
      ['Myanmar', ['asia'], 'mm', '95', '🇲🇲'],
      ['Namibia', ['africa'], 'na', '264', '🇳🇦'],
      ['Nauru', ['africa'], 'nr', '674', '🇳🇷'],
      ['Nepal', ['asia'], 'np', '977', '🇳🇵'],
      ['Netherlands', ['europe', 'eu-union'], 'nl', '31', '🇳🇱'],
      ['New Caledonia', ['oceania'], 'nc', '687', '🇳🇨'],
      ['New Zealand', ['oceania'], 'nz', '64', '🇳🇿'],
      ['Nicaragua', ['america', 'central-america'], 'ni', '505', '🇳🇮'],
      ['Niger', ['africa'], 'ne', '227', '🇳🇪'],
      ['Nigeria', ['africa'], 'ng', '234', '🇳🇬'],
      ['North Korea', ['asia'], 'kp', '850', '🇰🇵'],
      ['Norway', ['europe', 'baltic'], 'no', '47', '🇳🇴'],
      ['Oman', ['middle-east'], 'om', '968', '🇴🇲'],
      ['Pakistan', ['asia'], 'pk', '92', '🇵🇰'],
      ['Palau', ['oceania'], 'pw', '680', '🇵🇼'],
      ['Palestine', ['middle-east'], 'ps', '970', '🇵🇸'],
      ['Panama', ['america', 'central-america'], 'pa', '507', '🇵🇦'],
      ['Papua New Guinea', ['oceania'], 'pg', '675', '🇵🇬'],
      ['Paraguay', ['america', 'south-america'], 'py', '595', '🇵🇾'],
      ['Peru', ['america', 'south-america'], 'pe', '51', '🇵🇪'],
      ['Philippines', ['asia'], 'ph', '63', '🇵🇭'],
      ['Poland', ['europe', 'eu-union', 'baltic'], 'pl', '48', '🇵🇱'],
      ['Portugal', ['europe', 'eu-union'], 'pt', '351', '🇵🇹'],
      ['Puerto Rico', ['america', 'carribean'], 'pr', '1', '🇵🇷'],
      ['Qatar', ['middle-east'], 'qa', '974', '🇶🇦'],
      ['Réunion', ['africa'], 're', '262', '🇷🇪'],
      ['Romania', ['europe', 'eu-union'], 'ro', '40', '🇷🇴'],
      ['Russia', ['europe', 'asia', 'ex-ussr', 'baltic'], 'ru', '7', '🇷🇺'],
      ['Rwanda', ['africa'], 'rw', '250', '🇷🇼'],
      ['Saint Kitts and Nevis', ['america', 'carribean'], 'kn', '1869', '🇰🇳'],
      ['Saint Lucia', ['america', 'carribean'], 'lc', '1758', '🇱🇨'],
      ['Saint Vincent and the Grenadines', ['america', 'carribean'], 'vc', '1784', '🇻🇨'],
      ['Samoa', ['oceania'], 'ws', '685', '🇼🇸'],
      ['San Marino', ['europe'], 'sm', '378', '🇸🇲'],
      ['São Tomé and Príncipe', ['africa'], 'st', '239', '🇸🇹'],
      ['Saudi Arabia', ['middle-east'], 'sa', '966', '🇸🇦'],
      ['Senegal', ['africa'], 'sn', '221', '🇸🇳'],
      ['Serbia', ['europe', 'ex-yugos'], 'rs', '381', '🇷🇸'],
      ['Seychelles', ['africa'], 'sc', '248', '🇸🇨'],
      ['Sierra Leone', ['africa'], 'sl', '232', '🇸🇱'],
      ['Singapore', ['asia'], 'sg', '65', '🇸🇬'],
      ['Slovakia', ['europe', 'eu-union'], 'sk', '421', '🇸🇰'],
      ['Slovenia', ['europe', 'eu-union', 'ex-yugos'], 'si', '386', '🇸🇮'],
      ['Solomon Islands', ['oceania'], 'sb', '677', '🇸🇧'],
      ['Somalia', ['africa'], 'so', '252', '🇸🇴'],
      ['South Africa', ['africa'], 'za', '27', '🇿🇦'],
      ['South Korea', ['asia'], 'kr', '82', '🇰🇷'],
      ['South Sudan', ['africa', 'north-africa'], 'ss', '211', '🇸🇸'],
      ['Spain', ['europe', 'eu-union'], 'es', '34', '🇪🇸'],
      ['Sri Lanka', ['asia'], 'lk', '94', '🇱🇰'],
      ['Sudan', ['africa'], 'sd', '249', '🇸🇩'],
      ['Suriname', ['america', 'south-america'], 'sr', '597', '🇸🇷'],
      ['Swaziland', ['africa'], 'sz', '268', '🇸🇿'],
      ['Sweden', ['europe', 'eu-union', 'baltic'], 'se', '46', '🇸🇪'],
      ['Switzerland', ['europe'], 'ch', '41', '🇨🇭'],
      ['Syria', ['middle-east'], 'sy', '963', '🇸🇾'],
      ['Taiwan', ['asia'], 'tw', '886', '🇹🇼'],
      ['Tajikistan', ['asia', 'ex-ussr'], 'tj', '992', '🇹🇯'],
      ['Tanzania', ['africa'], 'tz', '255', '🇹🇿'],
      ['Thailand', ['asia'], 'th', '66', '🇹🇭'],
      ['Timor-Leste', ['asia'], 'tl', '670', '🇹🇱'],
      ['Togo', ['africa'], 'tg', '228', '🇹🇬'],
      ['Tonga', ['oceania'], 'to', '676', '🇹🇴'],
      ['Trinidad and Tobago', ['america', 'carribean'], 'tt', '1868', '🇹🇹'],
      ['Tunisia', ['africa', 'north-africa'], 'tn', '216', '🇹🇳'],
      ['Turkey', ['europe'], 'tr', '90', '🇹🇷'],
      ['Turkmenistan', ['asia', 'ex-ussr'], 'tm', '993', '🇹🇲'],
      ['Tuvalu', ['asia'], 'tv', '688', '🇹🇻'],
      ['Uganda', ['africa'], 'ug', '256', '🇺🇬'],
      ['Ukraine', ['europe', 'ex-ussr'], 'ua', '380', '🇺🇦'],
      ['United Arab Emirates', ['middle-east'], 'ae', '971', '🇦🇪'],
      ['United Kingdom', ['europe', 'eu-union'], 'gb', '44', '🇬🇧'],
      ['United States', ['america', 'north-america'], 'us', '1', '🇺🇸'],
      ['Uruguay', ['america', 'south-america'], 'uy', '598', '🇺🇾'],
      ['Uzbekistan', ['asia', 'ex-ussr'], 'uz', '998', '🇺🇿'],
      ['Vanuatu', ['oceania'], 'vu', '678', '🇻🇺'],
      ['Vatican City', ['europe'], 'va', '39', '🇻🇦'],
      ['Venezuela', ['america', 'south-america'], 've', '58', '🇻🇪'],
      ['Vietnam', ['asia'], 'vn', '84', '🇻🇳'],
      ['Yemen', ['middle-east'], 'ye', '967', '🇾🇪'],
      ['Zambia', ['africa'], 'zm', '260', '🇿🇲'],
      ['Zimbabwe', ['africa'], 'zw', '263', '🇿🇼']
    ]

    leadForm.innerHTML = `
      <div style="padding: 28px 24px 20px; background: linear-gradient(135deg, ${theme.primaryColor} 0%, ${this.adjustBrightness(theme.primaryColor, -15)} 100%); color: white; display: flex; justify-content: space-between; align-items: flex-start; border-radius: 16px 16px 0 0;">
        <div style="flex: 1;">
          <h3 style="margin: 0 0 8px 0; font-size: 20px; font-weight: 700; letter-spacing: -0.025em;">Welcome! 👋</h3>
          <p style="margin: 0; font-size: 15px; opacity: 0.9; line-height: 1.4;">Let's get to know you better so we can provide personalized assistance</p>
        </div>
        <button class="close-btn" style="background: rgba(255,255,255,0.15); border: none; color: white; padding: 10px; border-radius: 8px; cursor: pointer; width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; transition: background-color 0.2s; margin-left: 16px; flex-shrink: 0;" onmouseover="this.style.background='rgba(255,255,255,0.25)'" onmouseout="this.style.background='rgba(255,255,255,0.15)'">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      
      <div style="flex: 1; padding: 24px 24px 24px; display: flex; flex-direction: column; gap: 24px; background: linear-gradient(to bottom, #fafbfc 0%, #ffffff 100%);">
        <form class="lead-form" style="display: flex; flex-direction: column; gap: 10px; flex: 1;">
          <div>
            <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #1f2937; font-size: 14px; letter-spacing: 0.01em;">
              Full Name <span style="color: #dc2626;">*</span>
            </label>
            <input 
              type="text" 
              name="name" 
              required
              style="width: 100%; padding: 14px 16px; border: 2px solid #e5e7eb; border-radius: 12px; font-size: 15px; transition: all 0.2s ease; outline: none; background: #ffffff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);"
              placeholder="Enter your full name"
            />
          </div>
          
          <div>
            <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #1f2937; font-size: 14px; letter-spacing: 0.01em;">
              Phone/WhatsApp <span style="color: #dc2626;">*</span>
            </label>
            <div style="position: relative;">
              <div style="display: flex; border: 2px solid #e5e7eb; border-radius: 12px; background: #ffffff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); transition: all 0.2s ease;" class="phone-input-wrapper">
                <button type="button" class="country-selector-btn" style="display: flex; align-items: center; gap: 6px; padding: 14px 12px; background: transparent; border: none; font-size: 15px; cursor: pointer; border-right: 1px solid #e5e7eb; min-width: 85px; outline: none;">
                  <span class="selected-flag">��</span>

                  <span class="selected-code">+880</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-left: auto;">
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </button>
                <input 
                  type="tel" 
                  name="phone"
                  required
                  style="flex: 1; padding: 14px 16px; border: none; background: transparent; font-size: 15px; outline: none;"
                  placeholder="1712 345 678"
                />
              </div>
              <div class="country-dropdown" style="position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: white; border: 2px solid #e5e7eb; border-radius: 12px; box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15); z-index: 1000; max-height: 280px; overflow: hidden; display: none;">
                <div style="padding: 12px; border-bottom: 1px solid #f3f4f6;">
                  <input type="text" class="country-search" placeholder="Search countries..." style="width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; outline: none;" />
                </div>
                <div class="country-list" style="max-height: 220px; overflow-y: auto; scrollbar-width: thin; scrollbar-color: #cbd5e1 #f1f5f9;">
                  <!-- Countries will be populated here -->
                </div>
              </div>
              <input type="hidden" name="countryCode" value="+880" />
            </div>
         
          </div>
          
          <div>
            <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #1f2937; font-size: 14px; letter-spacing: 0.01em;">
              Email Address (Optional)
            </label>
            <input 
              type="email" 
              name="email"
              style="width: 100%; padding: 14px 16px; border: 2px solid #e5e7eb; border-radius: 12px; font-size: 15px; transition: all 0.2s ease; outline: none; background: #ffffff; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);"
              placeholder="your.email@example.com"
            />
          </div>
          
          <div style="margin-top: auto; padding-top: 8px;">
            <button 
              type="submit" 
              class="submit-btn"
              style="width: 100%; padding: 16px; background: linear-gradient(135deg, ${theme.primaryColor} 0%, ${this.adjustBrightness(theme.primaryColor, -10)} 100%); color: white; border: none; border-radius: 12px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; box-shadow: 0 4px 12px ${theme.primaryColor}30; letter-spacing: 0.01em;"
              onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 16px ${theme.primaryColor}40'"
              onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px ${theme.primaryColor}30'"
            >
              Start Chatting 🚀
            </button>
            <p style="margin: 12px 0 0 0; font-size: 12px; color: #9ca3af; text-align: center; line-height: 1.4;">
              By continuing, you agree to receive communications from us
            </p>
          </div>
        </form>
      </div>
    `

    // Add event listeners
    const closeBtn = leadForm.querySelector('.close-btn') as HTMLButtonElement
    const form = leadForm.querySelector('.lead-form') as HTMLFormElement
    const nameInput = leadForm.querySelector('input[name="name"]') as HTMLInputElement
    const emailInput = leadForm.querySelector('input[name="email"]') as HTMLInputElement
    const phoneInput = leadForm.querySelector('input[name="phone"]') as HTMLInputElement
    const countryCodeInput = leadForm.querySelector('input[name="countryCode"]') as HTMLInputElement
    const countrySelectorBtn = leadForm.querySelector('.country-selector-btn') as HTMLButtonElement
    const countryDropdown = leadForm.querySelector('.country-dropdown') as HTMLElement
    const countrySearch = leadForm.querySelector('.country-search') as HTMLInputElement
    const countryList = leadForm.querySelector('.country-list') as HTMLElement
    const selectedFlag = leadForm.querySelector('.selected-flag') as HTMLElement
    const selectedCode = leadForm.querySelector('.selected-code') as HTMLElement

    const phoneInputWrapper = leadForm.querySelector('.phone-input-wrapper') as HTMLElement

    // Initialize country list
    let filteredCountries = [...countries]
    let highlightedIndex = -1

    const populateCountryList = (searchTerm = '') => {
      filteredCountries = countries.filter(country => 
        (country[0] as string).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (country[3] as string).includes(searchTerm)
      )
      
      countryList.innerHTML = filteredCountries.map((country, index) => `
        <div class="country-item" data-index="${index}" style="display: flex; align-items: center; gap: 12px; padding: 12px 16px; cursor: pointer; transition: background-color 0.2s; ${index === highlightedIndex ? 'background-color: #f3f4f6;' : ''}" onmouseover="this.style.backgroundColor='#f3f4f6'" onmouseout="this.style.backgroundColor='${index === highlightedIndex ? '#f3f4f6' : 'transparent'}'">
          <span style="font-size: 18px;">${country[4]}</span>
          <div style="flex: 1;">
            <div style="font-weight: 500; color: #1f2937; font-size: 14px;">${country[0]}</div>
            <div style="color: #6b7280; font-size: 12px;">+${country[3]}</div>
          </div>
        </div>
      `).join('')
      
      // Add webkit scrollbar styling
      const style = document.createElement('style')
      style.textContent = `
        .country-list::-webkit-scrollbar {
          width: 6px;
        }
        .country-list::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 3px;
        }
        .country-list::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .country-list::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `
      if (!document.head.querySelector('style[data-country-scroll]')) {
        style.setAttribute('data-country-scroll', 'true')
        document.head.appendChild(style)
      }
      
      // Add click listeners to country items
      countryList.querySelectorAll('.country-item').forEach((item, index) => {
        item.addEventListener('click', () => selectCountry(filteredCountries[index]))
      })
      
      highlightedIndex = -1
    }

    const selectCountry = (country: any) => {
      selectedFlag.textContent = country[4]
      selectedCode.textContent = `+${country[3]}`
      countryCodeInput.value = `+${country[3]}`
      countryDropdown.style.display = 'none'
      phoneInput.focus()
    }

    const highlightCountry = (index: number) => {
      highlightedIndex = Math.max(0, Math.min(index, filteredCountries.length - 1))
      const items = countryList.querySelectorAll('.country-item')
      items.forEach((item, i) => {
        const element = item as HTMLElement
        element.style.backgroundColor = i === highlightedIndex ? '#f3f4f6' : 'transparent'
      })
      
      // Scroll to highlighted item
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' })
      }
    }

    // Country selector events
    countrySelectorBtn.addEventListener('click', (e) => {
      e.preventDefault()
      const isOpen = countryDropdown.style.display !== 'none'
      countryDropdown.style.display = isOpen ? 'none' : 'block'
      if (!isOpen) {
        populateCountryList()
        countrySearch.focus()
      }
    })

    // Search functionality
    countrySearch.addEventListener('input', (e) => {
      const searchTerm = (e.target as HTMLInputElement).value
      populateCountryList(searchTerm)
    })

    // Keyboard navigation
    countrySearch.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          highlightCountry(highlightedIndex + 1)
          break
        case 'ArrowUp':
          e.preventDefault()
          highlightCountry(highlightedIndex - 1)
          break
        case 'Enter':
          e.preventDefault()
          if (highlightedIndex >= 0 && filteredCountries[highlightedIndex]) {
            selectCountry(filteredCountries[highlightedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          countryDropdown.style.display = 'none'
          break
      }
    })

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (!leadForm.contains(target)) {
        countryDropdown.style.display = 'none'
      }
    })

    // Initialize with Bangladesh as default
    populateCountryList()
    
    // Set Bangladesh as default country
    const bangladeshCountry = countries.find(country => country[0] === 'Bangladesh')
    if (bangladeshCountry) {
      selectCountry(bangladeshCountry)
    }

    // Close button
    closeBtn.addEventListener('click', () => {
      this.closeChat()
    })

    // Form submission
    form.addEventListener('submit', async (e) => {
      e.preventDefault()
      
      const name = nameInput.value.trim()
      const phone = phoneInput.value.trim()
      
      // Validate required fields
      let hasError = false
      
      if (!name) {
        nameInput.style.borderColor = '#dc2626'
        nameInput.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.1)'
        hasError = true
      } else {
        nameInput.style.borderColor = '#10b981'
        nameInput.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)'
      }
      
      if (!phone) {
        phoneInputWrapper.style.borderColor = '#dc2626'
        phoneInputWrapper.style.boxShadow = '0 0 0 3px rgba(220, 38, 38, 0.1)'
        hasError = true
      } else {
        phoneInputWrapper.style.borderColor = '#10b981'
        phoneInputWrapper.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)'
      }
      
      if (hasError) {
        // Focus on first error field
        if (!name) {
          nameInput.focus()
        } else if (!phone) {
          phoneInput.focus()
        }
        return
      }

      const email = emailInput.value.trim() || undefined
      const countryCode = countryCodeInput.value
      const fullPhone = `${countryCode}${phone}`

      await this.submitLeadForm({ name, email, phone: fullPhone })
    })

    // Input focus and validation effects
    const inputs = [nameInput, emailInput]
    inputs.forEach(input => {
      input.addEventListener('focus', () => {
        input.style.borderColor = theme.primaryColor
        input.style.boxShadow = `0 0 0 3px ${theme.primaryColor}20`
      })
      input.addEventListener('blur', () => {
        input.style.borderColor = '#e5e7eb'
        input.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
      })
      
      // Real-time validation feedback
      input.addEventListener('input', () => {
        if (input.value.trim()) {
          input.style.borderColor = '#10b981'
          input.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)'
        } else if (input.required) {
          input.style.borderColor = '#e5e7eb'
          input.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
        }
      })
    })

    // Phone input wrapper styling
    phoneInput.addEventListener('focus', () => {
      phoneInputWrapper.style.borderColor = theme.primaryColor
      phoneInputWrapper.style.boxShadow = `0 0 0 3px ${theme.primaryColor}20`
    })
    phoneInput.addEventListener('blur', () => {
      phoneInputWrapper.style.borderColor = '#e5e7eb'
      phoneInputWrapper.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
    })

    // Phone input validation
    phoneInput.addEventListener('input', () => {
      if (phoneInput.value.trim()) {
        phoneInputWrapper.style.borderColor = '#10b981'
        phoneInputWrapper.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)'
      } else {
        phoneInputWrapper.style.borderColor = '#e5e7eb'
        phoneInputWrapper.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
      }
    })

    // Country selector focus handling for wrapper
    countrySelectorBtn.addEventListener('focus', () => {
      phoneInputWrapper.style.borderColor = theme.primaryColor
      phoneInputWrapper.style.boxShadow = `0 0 0 3px ${theme.primaryColor}20`
    })
    countrySelectorBtn.addEventListener('blur', () => {
      setTimeout(() => {
        if (document.activeElement !== phoneInput) {
          phoneInputWrapper.style.borderColor = '#e5e7eb'
          phoneInputWrapper.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
        }
      }, 100)
    })

    // Auto-detect country code when typing in phone input
    const detectCountryFromInput = (phoneValue: string) => {
      // Remove all non-digit characters
      const digits = phoneValue.replace(/\D/g, '')
      
      // Check if it starts with + followed by digits
      if (phoneValue.startsWith('+')) {
        const codeWithoutPlus = digits
        
        // Try to match country codes (longest first to avoid conflicts)
        const sortedCountries = [...countries].sort((a, b) => (b[3] as string).length - (a[3] as string).length)
        
        for (const country of sortedCountries) {
          const code = country[3] as string
          if (codeWithoutPlus.startsWith(code)) {
            // Update the selector
            selectedFlag.textContent = country[4] as string
            selectedCode.textContent = `+${code}`
            countryCodeInput.value = `+${code}`
            
            // Update the phone input to show only the number part
            const numberPart = codeWithoutPlus.substring(code.length)
            phoneInput.value = numberPart
            break
          }
        }
      } else if (digits.length > 0) {
        // If user types digits without +, try to detect based on length and patterns
        const sortedCountries = [...countries].sort((a, b) => (b[3] as string).length - (a[3] as string).length)
        
        for (const country of sortedCountries) {
          const code = country[3] as string
          if (digits.startsWith(code)) {
            selectedFlag.textContent = country[4] as string
            selectedCode.textContent = `+${code}`
            countryCodeInput.value = `+${code}`
            
            // Update the phone input to show only the number part
            const numberPart = digits.substring(code.length)
            phoneInput.value = numberPart
            break
          }
        }
      }
    }

    // Add special listener for phone input to detect country codes
    phoneInput.addEventListener('input', (e) => {
      const input = e.target as HTMLInputElement
      const value = input.value
      
      // Only auto-detect if user is typing a phone number that looks like it includes country code
      if (value.startsWith('+') || (value.replace(/\D/g, '').length > 10)) {
        detectCountryFromInput(value)
      }
      
      // Regular validation
      if (input.value.trim()) {
        input.style.borderColor = '#10b981'
        input.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)'
      } else {
        input.style.borderColor = '#e5e7eb'
        input.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
      }
    })

    // Country selector styling
    countrySelectorBtn.addEventListener('focus', () => {
      countrySelectorBtn.style.borderColor = theme.primaryColor
      countrySelectorBtn.style.boxShadow = `0 0 0 3px ${theme.primaryColor}20`
    })
    countrySelectorBtn.addEventListener('blur', () => {
      countrySelectorBtn.style.borderColor = '#e5e7eb'
      countrySelectorBtn.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)'
    })

    return leadForm
  }

  private async submitLeadForm(leadData: any): Promise<void> {
    try {
      const config = this.config.getConfig()
      const response = await this.apiClient.submitLead(config.apiKey, leadData)
      
      if (response.success) {
        // Mark lead as submitted
        this.state.leadSubmitted = true
        this.state.showLeadForm = false
        this.setLeadSubmissionStatus(true)
        
        // Show success message and switch to chat
        this.showLeadSuccessAndStartChat()
      } else {
        // Show error
        console.error('Lead submission failed:', response.error)
        // You could show an error message here
      }
    } catch (error) {
      console.error('Lead submission error:', error)
      // You could show an error message here
    }
  }

  private showLeadSuccessAndStartChat(): void {
    // Get the submitted name from the last form submission for personalization
    const form = this.window?.querySelector('.lead-form') as HTMLFormElement
    const nameInput = form?.querySelector('input[name="name"]') as HTMLInputElement
    const userName = nameInput?.value.trim() || 'there'
    
    // Add a personalized welcome message from the bot
    const welcomeMessage: ChatMessage = {
      id: Date.now().toString(),
      content: `Hi ${userName}! 👋 Thank you for providing your information. I'm here to help you with any questions about our products and services. How can I assist you today?`,
      role: 'assistant',
      timestamp: new Date(),
    }
    
    this.state.messages.push(welcomeMessage)
    
    // Remove existing window and create new chat window
    if (this.window && this.container) {
      this.container.removeChild(this.window)
      this.window = null
    }
    
    // Re-render as chat window
    this.renderWindow()
  }

  private renderBubble(): void {
    // Remove existing bubble if it exists
    if (this.bubble) {
      this.bubble.remove()
    }

    const theme = this.config.getTheme()
    
    this.bubble = createChatBubble({
      theme,
      onClick: () => this.toggleChat(),
      isOpen: this.state.isOpen,
      unreadCount: 0, // TODO: Implement unread count
    })

    if (this.container) {
      this.container.appendChild(this.bubble)
    }
  }

  private renderWindow(): void {
    const theme = this.config.getTheme()
    const config = this.config.getConfig()
    
    if (!this.window) {
      // Create window for the first time
      if (this.state.showLeadForm) {
        // Show lead form first
        this.window = this.createLeadForm(theme)
      } else {
        // Show regular chat window
        this.window = createChatWindow({
          theme,
          messages: this.state.messages,
          isLoading: this.state.isLoading,
          welcomeMessage: config.welcomeMessage,
          placeholder: config.placeholder,
          onSendMessage: (message) => this.sendMessage(message),
          onClose: () => this.closeChat(),
          onNewChat: () => this.startNewSession(),
        })
      }
      
      if (this.container) {
        this.container.appendChild(this.window)
      }
    } else {
      // Update existing window instead of recreating
      if (this.state.showLeadForm) {
        // Replace with lead form
        if (this.container) {
          this.container.removeChild(this.window)
          this.window = this.createLeadForm(theme)
          this.container.appendChild(this.window)
        }
      } else {
        // Update chat window
        this.updateChatWindow()
      }
    }
  }

  private updateChatWindow(): void {
    if (!this.window) return

    const messagesArea = this.window.querySelector('.messages-area') as HTMLElement
    if (!messagesArea) return

    // Remember scroll position
    const wasNearBottom = messagesArea.scrollHeight - messagesArea.scrollTop - messagesArea.clientHeight < 100

    // Clear existing messages
    messagesArea.innerHTML = ''

    const theme = this.config.getTheme()
    const config = this.config.getConfig()

    // Add welcome message if no messages
    if (this.state.messages.length === 0) {
      const welcomeDiv = document.createElement('div')
      Object.assign(welcomeDiv.style, {
        backgroundColor: '#ffffff',
        padding: '16px 20px',
        borderRadius: '16px 16px 16px 4px',
        marginBottom: '16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: '85%',
        fontSize: '15px',
        lineHeight: '1.5',
        color: '#374151',
        border: '1px solid #f3f4f6',
        position: 'relative',
      })
      
      const borderDiv = document.createElement('div')
      Object.assign(borderDiv.style, {
        position: 'absolute',
        top: '-1px',
        left: '-1px',
        right: '-1px',
        bottom: '-1px',
        background: `linear-gradient(135deg, ${theme.primaryColor}20, transparent)`,
        borderRadius: '16px 16px 16px 4px',
        zIndex: '-1',
      })
      welcomeDiv.appendChild(borderDiv)
      welcomeDiv.textContent = config.welcomeMessage
      messagesArea.appendChild(welcomeDiv)
    }

    // Add all messages
    this.state.messages.forEach((message, index) => {
      const messageDiv = this.createMessageElement(message, index, theme)
      messagesArea.appendChild(messageDiv)
    })

    // Add loading indicator if needed
    if (this.state.isLoading) {
      const loadingDiv = this.createLoadingElement(theme)
      messagesArea.appendChild(loadingDiv)
    }

    // Only scroll if user was near bottom
    if (wasNearBottom) {
      setTimeout(() => {
        messagesArea.scrollTo({
          top: messagesArea.scrollHeight,
          behavior: 'smooth'
        })
      }, 50)
    }
  }

  private createMessageElement(message: ChatMessage, index: number, theme: any): HTMLElement {
    const messageDiv = document.createElement('div')
    Object.assign(messageDiv.style, {
      marginBottom: '16px',
      display: 'flex',
      justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
      alignItems: 'flex-end',
      gap: '8px',
    })

    // Add avatar for bot messages
    if (message.role === 'assistant') {
      const avatar = document.createElement('div')
      Object.assign(avatar.style, {
        width: '32px',
        height: '32px',
        backgroundColor: theme.primaryColor,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: '0',
        marginBottom: '2px',
      })
      avatar.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
      messageDiv.appendChild(avatar)
    }

    const messageBubble = document.createElement('div')
    const isUser = message.role === 'user'
    Object.assign(messageBubble.style, {
      backgroundColor: isUser ? theme.primaryColor : '#ffffff',
      color: isUser ? '#ffffff' : '#374151',
      padding: '12px 16px',
      borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
      maxWidth: '75%',
      boxShadow: isUser 
        ? `0 4px 12px ${theme.primaryColor}25, 0 2px 4px rgba(0, 0, 0, 0.1)` 
        : '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1)',
      fontSize: '15px',
      lineHeight: '1.5',
      border: isUser ? 'none' : '1px solid #f3f4f6',
      wordWrap: 'break-word',
      position: 'relative',
    })

    if (isUser) {
      messageBubble.style.background = `linear-gradient(135deg, ${theme.primaryColor} 0%, ${this.adjustBrightness(theme.primaryColor, -5)} 100%)`
    }

    // Format message content based on role
    if (isUser) {
      messageBubble.textContent = message.content
    } else {
      // Format AI response with rich formatting
      messageBubble.innerHTML = this.formatAIMessage(message.content)
      messageBubble.classList.add('message-content')
    }

    messageDiv.appendChild(messageBubble)
    return messageDiv
  }

  private formatAIMessage(content: string): string {
    // Escape HTML to prevent XSS, then apply formatting
    const escapeHtml = (text: string) => {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    }

    let formatted = escapeHtml(content)

    // Normalize all bullet styles to dashes BEFORE other formatting
    formatted = formatted.replace(/^[•\*]\s+/gm, '- ')

    // Extract suggestions before formatting
    let suggestions: string[] = []
    const suggestionsMatch = formatted.match(/\*\*You might also want to ask:\*\*([\s\S]*?)(?:\n\n|$)/)
    if (suggestionsMatch) {
      const suggestionsText = suggestionsMatch[1]
      suggestions = suggestionsText.match(/^[•\-\*]\s+(.+)$/gm)?.map(s => s.replace(/^[•\-\*]\s+/, '').trim()) || []
      // Remove suggestions from main content
      formatted = formatted.replace(/\*\*You might also want to ask:\*\*[\s\S]*$/, '').trim()
    }

    // Format bold text (**text** or __text__)
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>')

    // Format italic text (*text* or _text_)
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>')
    formatted = formatted.replace(/_(.*?)_/g, '<em>$1</em>')

    // Format inline code (`code`)
    formatted = formatted.replace(/`(.*?)`/g, '<code style="background: #f3f4f6; padding: 2px 4px; border-radius: 3px; font-family: monospace; font-size: 13px;">$1</code>')

    // Format contact information sections FIRST (before bullet formatting)
    formatted = formatted.replace(/^-\s*\*\*Email:\*\*\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gmi, '<div class="contact-item"><strong>Email:</strong><a href="mailto:$1">$1</a></div>')
    formatted = formatted.replace(/^-\s*\*\*Website:\*\*\s*(https?:\/\/[^\s]+)/gmi, '<div class="contact-item"><strong>Website:</strong><a href="$1" target="_blank" rel="noopener noreferrer">$1</a></div>')
    formatted = formatted.replace(/^-\s*\*\*Phone:\*\*\s*(\+?[\d\s\-\(\)]+)/gmi, '<div class="contact-item"><strong>Phone:</strong><a href="tel:$1">$1</a></div>')
    formatted = formatted.replace(/^-\s*\*\*Contact Email:\*\*\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gmi, '<div class="contact-item"><strong>Contact Email:</strong><a href="mailto:$1">$1</a></div>')
    // Also handle cases without bold formatting
    formatted = formatted.replace(/^-\s*Email:\s*([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gmi, '<div class="contact-item"><strong>Email:</strong><a href="mailto:$1">$1</a></div>')
    formatted = formatted.replace(/^-\s*Website:\s*(https?:\/\/[^\s]+)/gmi, '<div class="contact-item"><strong>Website:</strong><a href="$1" target="_blank" rel="noopener noreferrer">$1</a></div>')

    // Format nested sub-items first (indented with spaces and dashes)
    formatted = formatted.replace(/^(\s+)-\s+(.+)$/gm, '<div style="display: flex; align-items: flex-start; margin: 1px 0; margin-left: 16px;"><span style="color: #6b7280; margin-right: 6px; margin-top: 1px; font-size: 12px;">-</span><span style="font-size: 14px;">$2</span></div>')
    
    // Format main bullet points (- item, * item, or • item) - standardize to dash
    formatted = formatted.replace(/^[•\-\*]\s+(.+)$/gm, '<div style="display: flex; align-items: flex-start; margin: 3px 0;"><span style="color: #6b7280; margin-right: 6px; margin-top: 1px; font-weight: 500;">-</span><span>$1</span></div>')

    // Format numbered lists (1. item, 2. item, etc.)
    formatted = formatted.replace(/^(\d+)\.\s+(.+)$/gm, '<div style="display: flex; align-items: flex-start; margin: 3px 0;"><span style="color: #6b7280; margin-right: 6px; margin-top: 1px; font-weight: 500;">$1.</span><span>$2</span></div>')

    // Format line breaks (double newlines become single paragraph break)
    formatted = formatted.replace(/\n\n+/g, '<br>')
    formatted = formatted.replace(/\n/g, ' ')

    // Format headers (# Header, ## Header, etc.)
    formatted = formatted.replace(/^### (.*$)/gm, '<h4 style="margin: 8px 0 4px 0; font-size: 15px; font-weight: 600; color: #1f2937;">$1</h4>')
    formatted = formatted.replace(/^## (.*$)/gm, '<h3 style="margin: 10px 0 5px 0; font-size: 16px; font-weight: 600; color: #1f2937;">$1</h3>')
    formatted = formatted.replace(/^# (.*$)/gm, '<h2 style="margin: 12px 0 6px 0; font-size: 17px; font-weight: 700; color: #111827;">$1</h2>')

    // Format links [text](url)
    formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline;">$1</a>')

    // Format remaining email addresses and phone numbers (that weren't caught by contact formatting)
    formatted = formatted.replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1" style="color: #3b82f6; text-decoration: underline;">$1</a>')
    formatted = formatted.replace(/(\+?[\d\s\-\(\)]{10,})/g, '<a href="tel:$1" style="color: #3b82f6; text-decoration: underline;">$1</a>')

    // Format code blocks (```code```)
    formatted = formatted.replace(/```(.*?)```/gs, '<pre style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 4px; padding: 8px; margin: 6px 0; font-family: monospace; font-size: 13px; overflow-x: auto; white-space: pre-wrap;">$1</pre>')

    // Format horizontal rules (--- or ***)
    formatted = formatted.replace(/^(---|\*\*\*)$/gm, '<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 10px 0;">')

    // Format contact information sections
    formatted = formatted.replace(/(Contact|Email|Phone|Website|Address):/gi, '<strong style="color: #1f2937;">$1:</strong>')

    // Add suggestions if found
    if (suggestions.length > 0) {
      const suggestionsHtml = suggestions.map(suggestion => 
        `<button class="suggestion-btn" style="background: #f3f4f6; border: 1px solid #d1d5db; border-radius: 12px; padding: 6px 12px; margin: 3px 3px 3px 0; font-size: 13px; color: #374151; cursor: pointer; transition: all 0.2s ease; display: inline-block;" onmouseover="this.style.background='#e5e7eb'; this.style.borderColor='#9ca3af'" onmouseout="this.style.background='#f3f4f6'; this.style.borderColor='#d1d5db'" onclick="window.dispatchEvent(new CustomEvent('suggestionClick', {detail: '${suggestion.replace(/'/g, "\\'")}'}))">
          ${suggestion}
        </button>`
      ).join('')
      
      formatted += `<div style="margin-top: 12px; padding-top: 8px; border-top: 1px solid #f3f4f6;">
        <div style="font-size: 13px; color: #6b7280; margin-bottom: 6px; font-weight: 500;">You might also want to ask:</div>
        ${suggestionsHtml}
      </div>`
    }

    // Wrap content in a container with optimized spacing
    return `<div style="line-height: 1.5;">${formatted}</div>`
  }

  private createLoadingElement(theme: any): HTMLElement {
    const loadingDiv = document.createElement('div')
    Object.assign(loadingDiv.style, {
      display: 'flex',
      justifyContent: 'flex-start',
      marginBottom: '16px',
      alignItems: 'flex-end',
      gap: '8px',
    })

    const avatar = document.createElement('div')
    Object.assign(avatar.style, {
      width: '32px',
      height: '32px',
      backgroundColor: theme.primaryColor,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: '0',
      marginBottom: '2px',
    })
    avatar.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `

    const loadingBubble = document.createElement('div')
    Object.assign(loadingBubble.style, {
      backgroundColor: '#ffffff',
      padding: '16px 20px',
      borderRadius: '16px 16px 16px 4px',
      maxWidth: '75%',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.1)',
      border: '1px solid #f3f4f6',
    })
    loadingBubble.innerHTML = `
      <div style="display: flex; gap: 6px; align-items: center;">
        <div style="width: 8px; height: 8px; background: linear-gradient(45deg, ${theme.primaryColor}, ${this.adjustBrightness(theme.primaryColor, 20)}); border-radius: 50%; animation: typingPulse 1.4s ease-in-out infinite;"></div>
        <div style="width: 8px; height: 8px; background: linear-gradient(45deg, ${theme.primaryColor}, ${this.adjustBrightness(theme.primaryColor, 20)}); border-radius: 50%; animation: typingPulse 1.4s ease-in-out 0.2s infinite;"></div>
        <div style="width: 8px; height: 8px; background: linear-gradient(45deg, ${theme.primaryColor}, ${this.adjustBrightness(theme.primaryColor, 20)}); border-radius: 50%; animation: typingPulse 1.4s ease-in-out 0.4s infinite;"></div>
        <span style="margin-left: 8px; font-size: 13px; color: #6b7280; font-style: italic;">AI is typing...</span>
      </div>
    `
    
    loadingDiv.appendChild(avatar)
    loadingDiv.appendChild(loadingBubble)
    return loadingDiv
  }

  private adjustBrightness(hex: string, percent: number): string {
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  private toggleChat(): void {
    if (this.state.isOpen) {
      this.closeChat()
    } else {
      this.openChat()
    }
  }

  private openChat(): void {
    this.state.isOpen = true
    this.renderBubble() // Update bubble icon
    this.renderWindow()
  }

  private closeChat(): void {
    this.state.isOpen = false
    this.renderBubble() // Update bubble icon
    
    if (this.window) {
      this.window.remove()
      this.window = null
    }
  }

  private async sendMessage(message: string): Promise<void> {
    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: message,
      role: 'user',
      timestamp: new Date(),
    }
    
    this.state.messages.push(userMessage)
    this.state.isLoading = true
    this.renderWindow()

    try {
      // Send to API
      const response = await this.apiClient.sendMessage({
        apiKey: this.config.getConfig().apiKey,
        message,
        sessionId: this.state.sessionId,
      })

      this.state.isLoading = false

      if (response.success && response.reply) {
        // Update session ID from response if provided and store it
        if (response.sessionId && response.sessionId !== this.state.sessionId) {
          this.state.sessionId = response.sessionId
          this.storeSessionId(response.sessionId)
        }

        // Add AI response
        const aiMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: response.reply,
          role: 'assistant',
          timestamp: new Date(),
        }
        
        this.state.messages.push(aiMessage)
        this.renderWindow()
        
        // Re-focus input for better UX
        setTimeout(() => {
          const input = document.querySelector('.chat-window input') as HTMLInputElement
          if (input) {
            input.focus()
          }
        }, 100)
      } else {
        // Handle error
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: response.error || 'Sorry, I encountered an error. Please try again.',
          role: 'assistant',
          timestamp: new Date(),
        }
        
        this.state.messages.push(errorMessage)
        this.renderWindow()
        
        // Re-focus input for better UX
        setTimeout(() => {
          const input = document.querySelector('.chat-window input') as HTMLInputElement
          if (input) {
            input.focus()
          }
        }, 100)
      }
    } catch (error) {
      this.state.isLoading = false
      console.error('Chat error:', error)
      
      // Add error message
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I\'m having trouble connecting. Please check your internet connection and try again.',
        role: 'assistant',
        timestamp: new Date(),
      }
      
      this.state.messages.push(errorMessage)
      this.renderWindow()
      
      // Re-focus input for better UX
      setTimeout(() => {
        const input = document.querySelector('.chat-window input') as HTMLInputElement
        if (input) {
          input.focus()
        }
      }, 100)
    }
  }

  private addStyles(): void {
    // Add CSS animations and additional styles
    const style = document.createElement('style')
    style.textContent = `
      @keyframes pulse {
        0%, 100% {
          opacity: 0.4;
        }
        50% {
          opacity: 1;
        }
      }
      
      @keyframes typingPulse {
        0%, 50%, 100% {
          opacity: 0.4;
          transform: scale(0.8);
        }
        25% {
          opacity: 1;
          transform: scale(1);
        }
      }
      
      #conversea-widget * {
        box-sizing: border-box;
      }
      
      .chat-bubble:hover {
        transform: scale(1.1) !important;
      }
      
      .chat-window {
        animation: slideUp 0.3s ease-out;
      }
      
      @keyframes slideUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      
      /* Enhanced formatting styles for AI messages */
      #conversea-widget .message-content {
        word-break: break-word;
      }
      
      #conversea-widget .message-content strong {
        font-weight: 600;
        color: inherit;
      }
      
      #conversea-widget .message-content em {
        font-style: italic;
      }
      
      #conversea-widget .message-content h2,
      #conversea-widget .message-content h3,
      #conversea-widget .message-content h4 {
        margin-top: 12px;
        margin-bottom: 6px;
        font-weight: 600;
        line-height: 1.3;
      }
      
      #conversea-widget .message-content ul,
      #conversea-widget .message-content ol {
        margin: 8px 0;
        padding-left: 0;
      }
      
      #conversea-widget .message-content a {
        color: #3b82f6;
        text-decoration: underline;
        transition: color 0.2s ease;
      }
      
      #conversea-widget .message-content a:hover {
        color: #1d4ed8;
      }
      
      #conversea-widget .message-content pre {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        padding: 12px;
        margin: 8px 0;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 13px;
        overflow-x: auto;
        white-space: pre-wrap;
      }
      
      #conversea-widget .message-content code {
        background: #f3f4f6;
        padding: 2px 4px;
        border-radius: 3px;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 13px;
      }
      
      #conversea-widget .message-content hr {
        border: none;
        border-top: 1px solid #e5e7eb;
        margin: 16px 0;
      }
      
      /* Contact information styling */
      #conversea-widget .contact-info {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        padding: 12px;
        margin: 8px 0;
      }
      
      #conversea-widget .contact-item {
        display: flex;
        align-items: center;
        margin: 6px 0;
        padding: 4px 0;
      }
      
      #conversea-widget .contact-item strong {
        min-width: 80px;
        margin-right: 8px;
        color: #374151;
      }
      
      #conversea-widget .contact-item a {
        color: #3b82f6;
        text-decoration: none;
        transition: color 0.2s ease;
      }
      
      #conversea-widget .contact-item a:hover {
        color: #1d4ed8;
        text-decoration: underline;
      }
    `
    
    if (!document.head.querySelector('#conversea-widget-styles')) {
      style.id = 'conversea-widget-styles'
      document.head.appendChild(style)
    }
  }

  // Public API methods
  public destroy(): void {
    // Clean up window
    if (this.window) {
      this.window.remove()
      this.window = null
    }
    
    // Clean up bubble
    if (this.bubble) {
      this.bubble.remove()
      this.bubble = null
    }
    
    // Clean up container
    if (this.container) {
      this.container.remove()
      this.container = null
    }
    
    // Clean up styles (only if no other widgets exist)
    const styles = document.head.querySelector('#conversea-widget-styles')
    if (styles && !document.querySelector('#conversea-widget')) {
      styles.remove()
    }
    
    // Reset state
    this.state = {
      isOpen: false,
      isMinimized: false,
      isLoading: false,
      messages: [],
      error: null,
      sessionId: undefined,
      showLeadForm: false,
      leadSubmitted: false,
    }
    
    // Clear stored session
    this.clearStoredSession()
  }

  public startNewSession(): void {
    // Clear current session and messages
    this.state.sessionId = undefined
    this.state.messages = []
    this.clearStoredSession()
    
    // Re-render the window if it's open
    if (this.state.isOpen) {
      this.renderWindow()
    }
  }

  public updateConfig(updates: Partial<ChatWidgetConfig>): void {
    this.config.updateConfig(updates)
    
    // Re-render if widget is active
    if (this.container) {
      this.renderBubble()
      if (this.state.isOpen) {
        this.renderWindow()
      }
    }
  }

  public getState(): WidgetState {
    return { ...this.state }
  }
}
